/**
 * React Router stores the entry's position in `history.state.idx`: 0 means no earlier in-app
 * entry (direct link, notification), so going back would leave the app.
 */
export const hasInAppHistory = (historyState: unknown): boolean => {
  const idx = (historyState as { idx?: unknown } | null)?.idx;
  return typeof idx === "number" && idx > 0;
};

/** `target` if it's a safe internal path, mirroring the API (`googleLoginState.service.ts`). */
export const getSafeRedirectPath = (target: string | null | undefined): string | null => {
  if (
    typeof target !== "string" ||
    target.length > 2048 ||
    !target.startsWith("/") ||
    target.startsWith("//") ||
    Array.from(target).some((character) => character.charCodeAt(0) <= 32 || character === "\\")
  ) {
    return null;
  }
  if (target.startsWith("/auth/login") || target.startsWith("/logout")) {
    return null;
  }
  return target;
};

const MEMBERSHIP_RETURN_KEY = "membershipReturnTo";

/** Remembers where a restricted member was sent from, across the Chargebee round trip. */
export const rememberMembershipReturnPath = (target: string | null | undefined): void => {
  const path = getSafeRedirectPath(target);
  if (!path) {
    return;
  }
  try {
    window.sessionStorage.setItem(MEMBERSHIP_RETURN_KEY, path);
  } catch {
    // Storage unavailable: the member simply stays on the settings page
  }
};

export const takeMembershipReturnPath = (): string | null => {
  try {
    const path = getSafeRedirectPath(window.sessionStorage.getItem(MEMBERSHIP_RETURN_KEY));
    window.sessionStorage.removeItem(MEMBERSHIP_RETURN_KEY);
    return path;
  } catch {
    return null;
  }
};

/**
 * Running as an installed app, where new windows leave the app (on iOS, for Safari without the
 * session).
 */
export const isInstalledApp = (): boolean => {
  if (typeof window === "undefined") return false;
  const isStandalone = window.matchMedia?.("(display-mode: standalone)").matches ?? false;
  const isIOSStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone;
  return isStandalone || isIOSStandalone === true;
};

/** Same-site links (absolute or relative) become in-app paths; other sites stay external. */
export const resolveAppLink = (
  link: string,
  origin: string,
): { internalPath: string } | { externalUrl: string } => {
  const trimmed = link.trim();
  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const url = new URL(trimmed);
      if (url.origin === origin) {
        return { internalPath: `${url.pathname}${url.search}${url.hash}` };
      }
      return { externalUrl: url.toString() };
    } catch {
      return { externalUrl: trimmed };
    }
  }
  // "events/all" would resolve against the current page: treat it as an app path
  return { internalPath: trimmed.startsWith("/") ? trimmed : `/${trimmed}` };
};

export const getLoginUrl = (pathname: string, search = ""): string => {
  const redirectedFrom = getSafeRedirectPath(`${pathname}${search}`);
  if (!redirectedFrom || redirectedFrom === "/") {
    return "/auth/login";
  }
  return `/auth/login?${new URLSearchParams({ redirectedFrom }).toString()}`;
};

/** Lessons close to their topic's overview; the overview closes to the topics list. */
export const getTopicLessonCloseTarget = ({
  pathname,
  topicsPage,
  hasOverview,
}: {
  pathname: string;
  topicsPage?: string | null;
  hasOverview: boolean;
}): string | null => {
  const [, root, topicId, pageName] = pathname.split("/");
  if (root !== "topic" || !topicId) {
    return null;
  }
  const isOverview = pageName === "overview";
  if (hasOverview && pageName && !isOverview && pageName !== "lesson") {
    return `/topic/${topicId}/overview`;
  }
  return topicsPage ? `/topics/${topicsPage}` : null;
};
